import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Tooltip } from '../tooltip';
import { Input } from '../input/Input';

type Props = {
  id?: string,
  quantity: number,
  filled?: boolean,
  className?: string,
  disabled?: boolean,
  onClick?: Function,
  isEditable?: boolean,
  isEditing?: boolean,
  onChangeAmount?: Function,
  onInputFocus?: Function,
  onInputBlur?: Function,
  enteredAmountExceeded?: boolean,
  size?: 'xsmall',
};

export const CounterComponent = ({
  id,
  quantity = 0,
  filled = false,
  className,
  disabled,
  onClick,
  isEditable,
  isEditing,
  onChangeAmount,
  onInputFocus,
  onInputBlur,
  enteredAmountExceeded,
  size,
}: Props) => {
  const handleClick = e => {
    e.stopPropagation();

    if (disabled || !onClick) {
      return;
    }

    onClick(e);
  };

  const { t } = useTranslation('');

  const amountInput = () => {
    return (
      <InputWrapper
        value={quantity}
        handleChange={onChangeAmount}
        type="number"
        size="small"
        onFocus={e => {
          e.target.select();
          onInputFocus();
        }}
        onBlur={onInputBlur}
        autoFocus={isEditing}
        preventNonNumericEntry
        min="1"
        max="99"
        isQuantityField
        filled={filled}
      />
    );
  };

  let quantityToRender = quantity;

  if (isEditable) {
    quantityToRender =
      isEditing && enteredAmountExceeded ? (
        <Tooltip onCreate={tip => tip.show()} content={<span>{t('alerts:quantityimitExceeded')}</span>}>
          {amountInput()}
        </Tooltip>
      ) : (
        amountInput()
      );
  }

  return (
    <CounterWrapper
      id={id && `${id}-quantity`}
      className={className}
      isEditable={isEditable}
      filled={filled}
      size={size}
      data-test="quantity-counter-quantity"
      aria-label={t('common:quantity', { quantity })}
      onClick={handleClick}
    >
      {quantityToRender}
    </CounterWrapper>
  );
};

const CounterWrapper = styled('span')`
  position: relative;
  font-size: ${({ theme }) => theme.fontSize.sm};
  min-width: 12px;
  text-align: center;

  ${({ isEditable, size }) =>
    !isEditable &&
    `&::after {
      content: '';
      position: absolute;
      width: ${size === 'xsmall' ? '32px' : '40px'};
      height: ${size === 'xsmall' ? '32px' : '40px'};
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
    }`}

  ${({ theme, filled }) =>
    filled &&
    `
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius.circle};
    background: ${theme.color.brand};
    color: ${theme.color.white};
    display: flex;
    justify-content: center;
    align-items: center;
  `};

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    min-width: 24px;
    font-size: ${({ theme }) => theme.fontSize.xl};

    ${({ filled }) =>
      filled &&
      `
      height: 40px;
      width: 40px;
    `}
  }

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    min-width: 48px;

    ${({ filled }) =>
      filled &&
      `
      height: 48px;
    `}
  }
`;

const InputWrapper = styled(Input)`
  @media (${({ theme }) => theme.breakpoint.mobile}) {
    width: 30px;
    height: 30px;
    padding: 2px;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;
